import React, { useEffect, useRef, useState } from 'react';
import MainService from '../../services/MainService';
import { useListKeyboardNav } from '../../hooks/useListKeyboardNav';
import { buildAccountFilter, parseApiList } from '../../utils/accountingHelpers';
import { formControlClass } from '../../utils/formControls';
import { excelCellInputClass } from './accountingUi';

const defaultInputClass = formControlClass;

async function searchAccounts(source, term, searchField = 'Name') {
  const payload = buildAccountFilter(term, searchField);
  if (source === 'cash') return parseApiList(await MainService.GetCashAccounts(payload));
  if (source === 'bank') return parseApiList(await MainService.GetBankAccounts(payload));
  if (source === 'pdcPay') return parseApiList(await MainService.GetPDCPayableAccounts(payload));
  if (source === 'pdcRecv') return parseApiList(await MainService.GetPDCReceivableAccounts(payload));
  return parseApiList(await MainService.accountInAutoComplete(payload));
}

export function LedgerAccountAutocomplete({
  value,
  onSelect,
  placeholder = 'Search account by name',
  source = 'general',
  disabled = false,
  variant = 'default',
  displayField = 'name',
  searchField,
  className,
  onFocus,
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);

  const inputClass = className || (variant === 'cell' ? excelCellInputClass : defaultInputClass);
  const resolvedSearchField = searchField || (displayField === 'code' ? 'AccountCode' : 'Name');
  const display =
    displayField === 'code'
      ? value?.AccountCode || value?.accountCode || ''
      : value?.Name || value?.name || '';
  const listOpen = open && term.length >= 2 && !loading && items.length > 0;

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (term.length < 2) {
      setItems([]);
      return undefined;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        setItems(await searchAccounts(source, term, resolvedSearchField));
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [term, source, resolvedSearchField]);

  const handleSelect = (row) => {
    onSelect(row);
    setTerm('');
    setOpen(false);
  };

  const nav = useListKeyboardNav({
    isOpen: listOpen,
    itemCount: items.length,
    onSelectIndex: (index) => items[index] && handleSelect(items[index]),
    onClose: () => setOpen(false),
    resetKey: term,
  });

  return (
    <div className={`relative ${variant === 'cell' ? 'h-full min-h-[2.25rem]' : ''}`} ref={wrapRef}>
      <input
        type="text"
        className={inputClass}
        disabled={disabled}
        value={open ? term : display}
        placeholder={placeholder}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={(e) => {
          setTerm(display);
          setOpen(true);
          onFocus?.(e);
        }}
        onKeyDown={nav.onKeyDown}
      />
      {open && term.length >= 2 ? (
        <ul
          className="absolute z-50 mt-1 max-h-52 min-w-[16rem] overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          role="listbox"
        >
          {loading ? (
            <li className="px-3 py-2 text-sm text-gray-500">Searching…</li>
          ) : items.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-500">No accounts found</li>
          ) : (
            items.map((row, index) => (
              <li key={row.Id || index}>
                <button
                  type="button"
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${nav.activeIndex === index ? 'bg-primary/10' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(row)}
                >
                  <span className="font-medium text-gray-900">{row.Name}</span>
                  {row.AccountCode ? <span className="ml-2 text-gray-500">({row.AccountCode})</span> : null}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

export function GeneralAccountAutocomplete({ value, onSelect, placeholder, disabled }) {
  return (
    <LedgerAccountAutocomplete
      value={value}
      onSelect={onSelect}
      placeholder={placeholder || 'Search ledger account'}
      source="general"
      disabled={disabled}
    />
  );
}
